import { useEffect, useRef, useCallback, useMemo } from 'react';
import { PlusOutlined, DragOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import styles from './index.module.scss';
import Iconfont from '@utils/Iconfont';



const MAX_DEPTH = 5;

const MENU_ITEMS = [
    { key: 'createSub', label: '新建子文件夹', icon: <Iconfont type="icon-Material-NewFolderOutlined" /> },
    { key: 'move', label: '移动', icon: <Iconfont type="icon-Material-FolderMoveOutlined" /> },
    { key: 'rename', label: '重命名', icon: <Iconfont type="icon-Material-EditOutlined" /> },
    { key: 'delete', label: '删除', icon: <Iconfont type="icon-Material-DeleteOutlined" />, danger: true },
];

/**
 * 树节点右键菜单组件
 * @param {boolean} visible - 是否显示
 * @param {object} position - 菜单位置 { x, y }
 * @param {object} nodeData - 当前右键的节点数据
 * @param {function} onClose - 关闭菜单
 * @param {function} onAction - 菜单操作回调 (actionKey, nodeData) => void
 */
const TreeContextMenu = ({ visible, position, nodeData, onClose, onAction }) => {
    const menuRef = useRef(null);

    // 点击外部关闭
    const handleClickOutside = useCallback((e) => {
        if (menuRef.current && !menuRef.current.contains(e.target)) {
            onClose?.();
        }
    }, [onClose]);

    useEffect(() => {
        if (visible) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('scroll', onClose, true);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('scroll', onClose, true);
        };
    }, [visible, handleClickOutside, onClose]);

    // 根据深度和权限过滤菜单项
    const filteredItems = useMemo(() => {
        let items = MENU_ITEMS;

        // depth >= MAX_DEPTH 时隐藏"新建子文件夹"
        if (nodeData?.depth >= MAX_DEPTH) {
            items = items.filter(item => item.key !== 'createSub');
        }

        // canManage === false 时隐藏移动、重命名、删除
        if (nodeData?.canManage === false) {
            const disableKeys = ['move', 'rename', 'delete'];
            items = items.filter(item => !disableKeys.includes(item.key));
        }

        return items;
    }, [nodeData]);

    // 边界修正：防止菜单溢出视口
    const getAdjustedPosition = () => {
        const menuWidth = 200;
        const menuHeight = 140;
        let { x, y } = position || {};
        if (x + menuWidth > window.innerWidth) {
            x = window.innerWidth - menuWidth - 8;
        }
        if (y + menuHeight > window.innerHeight) {
            y = window.innerHeight - menuHeight - 8;
        }
        return { left: x, top: y };
    };

    if (!visible || !position) return null;

    const { left, top } = getAdjustedPosition();

    return (
        <div
            ref={menuRef}
            className={styles.contextMenuOverlay}
            style={{ left, top }}
        >
            {filteredItems.map((item) => (
                <div
                    key={item.key}
                    className={`${styles.menuItem} ${item.danger ? styles.danger : ''}`}
                    onClick={() => {
                        onAction?.(item.key, nodeData);
                        onClose?.();
                    }}
                >
                    <span className={styles.menuIcon}>{item.icon}</span>
                    <span className={styles.menuLabel}>{item.label}</span>
                </div>
            ))}
        </div>
    );
};

export default TreeContextMenu;
